// Budget month -> contract month entry for one commodity.
//
// Twelve rows, one per budget month. Each row picks a year (Previous / Current / Next,
// relative to the budget year) and exactly one contract month - the radio inputs share
// a name per row, so choosing a second one clears the first without any handler doing it.
//
// The grid is 14 columns wide, so the month columns are deliberately narrow: the header
// is a three letter abbreviation and the cell is nothing but the radio.
import {
  Box,
  Button,
  Chip,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import {
  CONTRACT_YEAR_OFFSETS,
  ContractMonthMappingRow,
  DEFAULT_CONTRACT_YEAR_OFFSET,
  MONTHS_IN_YEAR,
  getContractYearOffsetLabel,
  months,
  numberToShortMonth,
} from '../../../../Helpers';
import { StoneXSelect } from '../../../StoneXMui';

export interface ContractMonthMappingTableProps {
  rows: ContractMonthMappingRow[];
  disabled?: boolean;
  onChange: (rows: ContractMonthMappingRow[]) => void;
}

const CONTRACT_MONTHS = Array.from({ length: MONTHS_IN_YEAR }, (_, index) => index + 1);

export default function ContractMonthMappingTable(props: ContractMonthMappingTableProps) {
  const { rows, disabled, onChange } = props;

  const mappedCount = rows.filter((row) => row.contractMonth !== null).length;

  function updateRow(budgetMonth: number, patch: Partial<ContractMonthMappingRow>) {
    onChange(rows.map((row) => (row.budgetMonth === budgetMonth ? { ...row, ...patch } : row)));
  }

  function clearAll() {
    onChange(rows.map((row) => ({ ...row, contractMonth: null, yearOffset: DEFAULT_CONTRACT_YEAR_OFFSET })));
  }

  function resetToSameMonth() {
    onChange(
      rows.map((row) => ({
        ...row,
        contractMonth: row.budgetMonth,
        yearOffset: DEFAULT_CONTRACT_YEAR_OFFSET,
      })),
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <Chip size="small" label={`${mappedCount} of ${MONTHS_IN_YEAR} budget months mapped`} />
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" variant="text" onClick={resetToSameMonth} disabled={disabled}>
          Use same month
        </Button>
        <Button size="small" variant="text" onClick={clearAll} disabled={disabled}>
          Clear all
        </Button>
      </Box>

      <TableContainer sx={{ maxHeight: 460 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600, minWidth: 110 }}>Budget Month</TableCell>
              <TableCell sx={{ fontWeight: 600, minWidth: 130 }}>Contract Year</TableCell>
              {CONTRACT_MONTHS.map((month) => (
                <TableCell key={month} align="center" sx={{ fontWeight: 600, px: 0.5 }}>
                  {numberToShortMonth(month)}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.budgetMonth} hover>
                <TableCell>
                  <Typography variant="body2">{months[row.budgetMonth - 1]}</Typography>
                </TableCell>

                <TableCell>
                  <StoneXSelect
                    options={CONTRACT_YEAR_OFFSETS}
                    getId={(x) => x}
                    getOptionLabel={(x) => getContractYearOffsetLabel(x)}
                    value={row.yearOffset}
                    onChange={(value) =>
                      updateRow(row.budgetMonth, { yearOffset: value ?? DEFAULT_CONTRACT_YEAR_OFFSET })
                    }
                    disabled={disabled}
                    isControlled
                    fullWidth
                  />
                </TableCell>

                {CONTRACT_MONTHS.map((month) => (
                  <TableCell key={month} align="center" sx={{ px: 0.5 }}>
                    <Radio
                      size="small"
                      name={`contract-month-${row.budgetMonth}`}
                      checked={row.contractMonth === month}
                      disabled={disabled}
                      // Clicking the chosen month again clears the row, which is the
                      // only way to say "this budget month has no contract month".
                      onClick={() =>
                        updateRow(row.budgetMonth, {
                          contractMonth: row.contractMonth === month ? null : month,
                        })
                      }
                      readOnly
                      inputProps={{ 'aria-label': `${months[row.budgetMonth - 1]} maps to ${months[month - 1]}` }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Typography variant="caption" color="text.secondary">
        Contract Year is relative to the budget year - a November budget priced off the following
        March is Next. Click a selected month again to leave that budget month unmapped.
      </Typography>
    </Box>
  );
}
